import {
    ASSISTANT_TOOL_NAMES,
    ASSISTANT_TOOL_OUTPUT_STATUSES,
    ASSISTANT_TOOL_OUTPUT_TYPES,
} from "~components/outfitter-assistant/assistant-tool-constants"
import {
    getOutputRecord,
    type AnyToolUIPart,
} from "~components/shared/chat/tool-ui-part-helpers"

export type UserSelectionOption = {
    readonly id: string
    readonly label: string
    readonly subtitle?: string
    readonly imageUrl?: string
}

type UserSelectionPresentation = {
    readonly interactionId: string
    readonly prompt: string
    readonly options: readonly UserSelectionOption[]
    readonly mode: UserSelectionMode
}

export type UserSelectionMode = "single" | "multi"

const TOOL_OUTPUT_AVAILABLE_STATE = "output-available"
const DEFAULT_SELECTION_MODE: UserSelectionMode = "single"

export function getUserSelectionPresentation(
    part: AnyToolUIPart
): UserSelectionPresentation | null {
    if (part.state !== TOOL_OUTPUT_AVAILABLE_STATE) return null

    const output = getOutputRecord(part)
    if (!isUserSelectionAwaitingOutput(output)) return null

    const prompt = toNonEmptyString(output.prompt)
    const interactionId = toNonEmptyString(output.interactionId)
    const options = parseSelectionOptions(output.options)
    const mode = parseSelectionMode(output.mode)

    if (!prompt || !interactionId || !options.length) return null

    return { interactionId, prompt, options, mode }
}

export function isUserSelectionToolPart(part: AnyToolUIPart): boolean {
    return part.type === `tool-${ASSISTANT_TOOL_NAMES.REQUEST_SELECTION}`
}

function isUserSelectionAwaitingOutput(
    output: Readonly<Record<string, unknown>> | null
): output is Readonly<Record<string, unknown>> {
    if (!output) return false

    return (
        output.type === ASSISTANT_TOOL_OUTPUT_TYPES.USER_SELECTION &&
        output.status === ASSISTANT_TOOL_OUTPUT_STATUSES.NEEDS_USER_INPUT
    )
}

function parseSelectionOptions(value: unknown): UserSelectionOption[] {
    if (!Array.isArray(value)) return []

    return value.flatMap((entry) => {
        const record = toRecord(entry)
        if (!record) return []

        const id = toNonEmptyString(record.id)
        const label = toNonEmptyString(record.label)
        const subtitle = toOptionalString(record.subtitle)
        const imageUrl = toOptionalString(record.imageUrl)

        if (!id || !label) return []

        return [
            {
                id,
                label,
                ...(subtitle ? { subtitle } : {}),
                ...(imageUrl ? { imageUrl } : {}),
            },
        ]
    })
}

function parseSelectionMode(value: unknown): UserSelectionMode {
    return value === "multi" ? "multi" : DEFAULT_SELECTION_MODE
}

function toRecord(value: unknown): Readonly<Record<string, unknown>> | null {
    if (typeof value !== "object" || value === null) return null

    return Object.fromEntries(Object.entries(value))
}

function toNonEmptyString(value: unknown): string | null {
    return typeof value === "string" && value.trim() ? value.trim() : null
}

function toOptionalString(value: unknown): string | undefined {
    const parsed = toNonEmptyString(value)
    return parsed ?? undefined
}
